import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";

/**
 * The company row — the letterhead, the GSTIN, the bank block (§7, §9.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTED ONLY AS A COMMENT FOR A WEEK. The API has been complete
 * and tested since the backend phase; `endpoints.company` sat with ZERO callers
 * while every invoice printed "SELLER GSTIN:" followed by nothing. The billing
 * audit (2026-08-15) flagged it as the single worst gap in the app: a legally
 * invalid document produced silently.
 *
 * The response is the WHOLE row, nullable fields and all. That is deliberate:
 * the screen's job is precisely to show which legally required fields are still
 * blank, so a shape that hid the nulls would hide the problem.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * What `GET /company` answers.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOT FIELD-FOR-FIELD THE PRISMA ROW ANY MORE, AND THE FIVE OPTIONAL FIELDS ARE
 * WHY (2026-08-17). The route PROJECTS OUT the PAN and the whole bank block for
 * any caller without `settings.write` — see `routes/company.ts` for the
 * argument, but the short version is that `settings.view` is on the shared row
 * so that every render can reach the letterhead, and the day the CA got a login
 * that meant handing an external professional the business's bank account
 * number for the asking.
 *
 * They are `?`, not `| null`, deliberately: the server OMITS the keys rather
 * than nulling them, because `null` would say "this business has no bank
 * account" — a lie a preview could render and a person could believe — while an
 * absent key says "not for you". Marking them optional here is what makes that
 * distinction survive into the client's types instead of being discovered as an
 * `undefined` in a form field.
 *
 * Only `SettingsScreen` reads them, and that screen is behind `settings.write`,
 * so in practice the caller who sees `undefined` is a caller who cannot open the
 * screen at all.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface CompanySettings {
  id: number;
  legalName: string;
  tradeName: string | null;
  tagline: string | null;
  gstin: string | null;
  pan?: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  stateCode: string;
  stateName: string;
  pincode: string;
  phone: string;
  email: string;
  bankName?: string | null;
  bankAccountNo?: string | null;
  bankIfsc?: string | null;
  bankBranch?: string | null;
  logoUrl: string | null;
  signatureUrl: string | null;
  termsDefault: string | null;
  jurisdictionNote: string | null;
  computerGeneratedNote: string | null;
  declarationDefault: string | null;
  nextInvoiceSeq: number;
  nextQuoteSeq: number;
  currentFy: string;
  /*
   * THE CHALLAN SERIES CARRIES ITS OWN FINANCIAL-YEAR MARKER, and reading it
   * here is what lets `CountersCard` edit `challan_fy` WITHOUT going anywhere
   * near `current_fy`. They are two columns because they are two series with
   * two reset behaviours (services/numbering.ts, DOCUMENT_SERIES): the invoice
   * sequence restarts at 1 when `current_fy` rolls over on 1 April, and the
   * challan sequence restarts on `challan_fy` doing the same, independently. A
   * challan raised on 2 April moves `challan_fy` and nothing else; folding the
   * two into one field would restart the INVOICE series from that challan.
   */
  nextChallanSeq: number;
  challanFy: string;
}

export function useCompany() {
  return useQuery({
    queryKey: ["company", "settings"],
    queryFn: async () => api.get<{ data: CompanySettings }>(endpoints.company),
    select: (envelope) => envelope.data,
  });
}

export function useSaveCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.put<{ data: CompanySettings; warnings?: string[] }>(endpoints.company, body),
    onSuccess: () => {
      /*
       * Everything, not just ["company"]: the GSTIN decides the supply type of
       * every PREVIEW (§5), and the blank-GSTIN warning is embedded in cached
       * invoice previews. A narrower invalidation would keep showing the
       * warning after the fix that removes it.
       */
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * What `PUT /company/counters` actually answers — and it is NOT the company row.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * `routes/company.ts` ends with
 *   `res.json({ data: result.next, previous: result.previous, warnings })`
 * where both `next` and `previous` are the seven counter columns, and `warnings`
 * is a `string[]` composed by `counterWarnings` — one plain-English sentence per
 * consequence the endpoint could not prevent: a lowered sequence that will issue
 * numbers a SECOND time, a raised one that leaves a gap, a moved financial-year
 * marker. The endpoint returns 200 with them, deliberately: they are warnings,
 * not refusals (§5, §6.1).
 *
 * This was typed `{ data: CompanySettings }` — the shape of the OTHER endpoint
 * in this file — and nothing noticed, because the caller discarded the response
 * entirely. Naming it correctly is what makes `warnings` reachable from the
 * card without guessing, and `previous` is here because it is genuinely sent;
 * an interface that omitted it would invite somebody to "add" it later against
 * a server that has always answered with it.
 *
 * NOTHING ABOUT THE REQUEST OR THE SERVER CONTRACT CHANGES. This is a
 * description of what already arrives.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface CountersResponse {
  /**
   * The seven counter columns after the change, and before it. Typed loosely on
   * purpose: the API's own `SeriesCounters` lives in `apps/api/src/services`
   * and is not importable here, and hand-copying it into the SPA would create a
   * second definition of §5's series that can drift from the first. Nothing
   * reads these — the card refetches the company row instead — so a precise
   * duplicate would be a liability with no payer.
   */
  data: Record<string, number | string>;
  previous: Record<string, number | string>;
  /** One plain-English sentence per consequence. Absent means none, not "unknown". */
  warnings?: string[];
}

/**
 * §6 migration note — set the starting counters, with a reason and an explicit
 * confirmation, both demanded by the schema because this rewrites where a
 * legal series continues from.
 */
export function useSaveCounters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.put<CountersResponse>(endpoints.companyCounters, body),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}
